import { Injectable } from '@angular/core';
import { AfmUser, afmUserConverter } from 'src/app/shared/models/afm.models';
import { LocalizationLanguage } from 'src/app/shared/models/localization.model';
import { CraftConfiguration, DbCraft, dbCraftConverter } from 'src/app/shared/models/craft.model';
import { PriceFavoritesList } from 'src/app/shared/models/price-favorites.model';
import { TransportConfiguration } from 'src/app/shared/models/transport.model';
import { Location } from 'src/app/shared/models/location.model';
import { PriceConfiguration } from 'src/app/shared/models/price.model';
import { FlipperConfiguration } from 'src/app/shared/models/flipper.model';
import { FarmConfiguration } from 'src/app/shared/models/farming-models/farm-models';
import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  getDocs,
  Firestore,
  doc,
  getDoc,
  DocumentReference,
  addDoc,
  arrayRemove,
  arrayUnion,
  collectionGroup,
  count,
  deleteDoc,
  limit,
  orderBy,
  query,
  setDoc,
  sum,
  updateDoc,
  writeBatch,
  getAggregate,
} from 'firebase/firestore/lite';
import { User } from 'firebase/auth';
import { environment } from 'src/environments/environment';
import { PathfinderConfiguration } from 'src/app/shared/models/pathfinder.models';

@Injectable({
  providedIn: 'root',
})
export class FirestoreService {
  private readonly firebaseConfig = environment.firebaseConfig;
  app: FirebaseApp;
  fireStore: Firestore;
  constructor() {
    this.app = initializeApp(this.firebaseConfig);
    this.fireStore = getFirestore(this.app);
  }

  async getUser(user: User): Promise<AfmUser> {
    const docRef = doc(this.fireStore, 'users', user.uid).withConverter(afmUserConverter);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      const newUser = new AfmUser(
        user.uid,
        new Date(user.metadata.creationTime ?? new Date()),
        undefined,
        user.displayName ?? undefined,
        user.email ?? undefined,
        user.photoURL ?? undefined
      );

      // Save the new user
      await setDoc(docRef, newUser);
      return newUser;
    }
  }

  async deleteUserData(user: AfmUser): Promise<boolean> {
    try {
      const userDocRef = doc(this.fireStore, 'users', user.uId).withConverter(afmUserConverter);
      const craftsCollectionRef = collection(userDocRef, 'crafts');

      // Get all crafts documents
      const craftsSnapshot = await getDocs(craftsCollectionRef);

      // Delete all crafts documents
      const batch = writeBatch(this.fireStore);
      craftsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });

      // Delete the user document
      batch.delete(userDocRef);

      await batch.commit();

      return true;
    } catch (error) {
      console.error('Error deleting user data:', error);
      return false;
    }
  }

  async updateServer(user: AfmUser, server: string): Promise<void> {
    const docRef = doc(this.fireStore, 'users', user.uId).withConverter(afmUserConverter);
    await updateDoc(docRef, {
      'afmUserSettings.server': server,
    });
  }
  async updateTheme(user: AfmUser, theme: string): Promise<void> {
    const docRef = doc(this.fireStore, 'users', user.uId).withConverter(afmUserConverter);
    await updateDoc(docRef, {
      'afmUserSettings.theme': theme,
    });
  }

  async updateLanguage(user: AfmUser, language: LocalizationLanguage): Promise<void> {
    const docRef = doc(this.fireStore, 'users', user.uId).withConverter(afmUserConverter);
    await updateDoc(docRef, {
      'afmUserSettings.language': language,
    });
  }

  async updateSelectedCities(user: AfmUser, selectedCities: Location[]): Promise<void> {
    const docRef = doc(this.fireStore, 'users', user.uId).withConverter(afmUserConverter);
    await updateDoc(docRef, {
      'afmUserSettings.selectedCities': selectedCities,
    });
  }

  async updateCraftConfig(user: AfmUser, config: CraftConfiguration, specMap: Map<string, number>): Promise<void> {
    const docRef = doc(this.fireStore, 'users', user.uId).withConverter(afmUserConverter);
    await updateDoc(docRef, {
      'afmUserSettings.craftingConfiguration': JSON.parse(JSON.stringify(config)),
    });
    await updateDoc(docRef, {
      userSpecMap: Object.fromEntries(specMap),
    });
  }

  async updateFarmConfig(user: AfmUser, config: FarmConfiguration, specMap: Map<string, number>): Promise<void> {
    const docRef = doc(this.fireStore, 'users', user.uId).withConverter(afmUserConverter);
    await updateDoc(docRef, {
      'afmUserSettings.farmConfiguration': JSON.parse(JSON.stringify(config)),
    });
    await updateDoc(docRef, {
      userSpecMap: Object.fromEntries(specMap),
    });
  }

  async updateTransportConfig(user: AfmUser, config: TransportConfiguration): Promise<void> {
    const docRef = doc(this.fireStore, 'users', user.uId).withConverter(afmUserConverter);
    await updateDoc(docRef, {
      'afmUserSettings.transportConfiguration': JSON.parse(JSON.stringify(config)),
    });
  }

  async updatePriceConfig(user: AfmUser, config: PriceConfiguration): Promise<void> {
    const docRef = doc(this.fireStore, 'users', user.uId).withConverter(afmUserConverter);
    await updateDoc(docRef, {
      'afmUserSettings.priceConfiguration': JSON.parse(JSON.stringify(config)),
    });
  }

  async updatePathfinderConfig(user: AfmUser, config: PathfinderConfiguration): Promise<void> {
    const docRef = doc(this.fireStore, 'users', user.uId).withConverter(afmUserConverter);
    await updateDoc(docRef, {
      'afmUserSettings.pathfinderConfiguration': JSON.parse(JSON.stringify(config)),
    });
  }

  async updateFlipperConfig(user: AfmUser, config: FlipperConfiguration): Promise<void> {
    const docRef = doc(this.fireStore, 'users', user.uId).withConverter(afmUserConverter);
    await updateDoc(docRef, {
      'afmUserSettings.flipperConfiguration': JSON.parse(JSON.stringify(config)),
    });
  }

  async addCraft(user: AfmUser, craft: DbCraft): Promise<DocumentReference<DbCraft>> {
    const collectionRef = collection(this.fireStore, 'users', user.uId, 'crafts').withConverter(dbCraftConverter);
    const docRef = await addDoc(collectionRef, craft);
    return docRef as DocumentReference<DbCraft>;
  }

  async getUserCrafts(user: AfmUser, amount: number = 50): Promise<DbCraft[]> {
    if (amount > 50) amount = 50;
    const collectionRef = collection(this.fireStore, 'users', user.uId, 'crafts').withConverter(dbCraftConverter);
    const queryRef = query(collectionRef, orderBy('date', 'desc'), limit(amount));
    const querySnapshot = await getDocs(queryRef);
    const crafts: DbCraft[] = [];
    querySnapshot.forEach((doc) => {
      crafts.push(doc.data() as DbCraft);
    });
    return crafts;
  }

  async getUserCraftsTotals(user: AfmUser): Promise<{ count: number; totalIncome: number; totalCost: number }> {
    const collectionRef = collection(this.fireStore, 'users', user.uId, 'crafts').withConverter(dbCraftConverter);
    const snapShot = await getAggregate(collectionRef, {
      count: count(),
      totalIncome: sum('totalIncome'),
      totalCost: sum('totalCost'),
    });
    return snapShot.data();
  }

  async getAllCraftsTotals(): Promise<{ count: number; totalIncome: number; totalCost: number }> {
    const collectionRef = collectionGroup(this.fireStore, 'crafts').withConverter(dbCraftConverter);
    const snapShot = await getAggregate(collectionRef, {
      count: count(),
      totalIncome: sum('totalIncome'),
      totalCost: sum('totalCost'),
    });
    return snapShot.data();
  }

  async deleteCraft(user: AfmUser, craft: DbCraft): Promise<void> {
    if (!craft.craftId) {
      throw new Error('Craft ID is undefined');
    }

    const docRef = doc(this.fireStore, 'users', user.uId, 'crafts', craft.craftId);
    await deleteDoc(docRef);
  }

  async addPriceFavoritesList(user: AfmUser, list: PriceFavoritesList): Promise<void> {
    const docRef = doc(this.fireStore, 'users', user.uId).withConverter(afmUserConverter);
    await updateDoc(docRef, {
      priceFavoritesLists: arrayUnion(JSON.parse(JSON.stringify(list))),
    });
  }

  async deletePriceFavoritesList(user: AfmUser, list: PriceFavoritesList): Promise<void> {
    const docRef = doc(this.fireStore, 'users', user.uId).withConverter(afmUserConverter);
    await updateDoc(docRef, {
      priceFavoritesLists: arrayRemove(JSON.parse(JSON.stringify(list))),
    });
  }
}
